'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Trash2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LazyImage, NoImagePlaceholder, PriceDisplay } from '@/components/partials';
import { Link } from '@/i18n/navigation';
import { getErrorMessage, getLocalizedValue, getResizedPictureUrl } from '@/lib/utils';
import { useCartStore } from '@/store';
import { useTranslatedRoute, useAppLocale } from '@/hooks';
import { CartItemType } from '@/types/cart';
type PropsType = {
  item: CartItemType;
};
const CartItemCard = ({
  item
}: PropsType) => {
  const t = useTranslations();
  const {
    backendLocale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  const {
    updateItem,
    removeItem
  } = useCartStore();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [qty, setQty] = useState(item.quantity);
  useEffect(() => {
    setQty(item.quantity);
  }, [item.quantity]);
  useEffect(() => {
    if (qty === item.quantity || qty < 1 || qty > item.product.stock) {
      return;
    }
    const timer = setTimeout(async () => {
      try {
        await updateItem(item.product.id, {
          quantity: qty
        });
        toast.success(t('item_updated_success'));
      } catch (error: any) {
        toast.error(getErrorMessage(error, t('something_went_wrong')));
        setQty(item.quantity);
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);
  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeItem(item.product.id);
      toast.success(t('item_removed_success'));
      setDeleteOpen(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('something_went_wrong')));
    } finally {
      setRemoving(false);
    }
  };
  const translations = item.product.translations;
  const localeKey = Object.keys(translations)[0];
  const productTitle = localeKey ? translations[localeKey]?.title ?? item.product.code : item.product.code;
  const price = item.product.price;

  // Build product detail URL
  const productSlug = getLocalizedValue(item.product, 'slug', backendLocale);
  const category = item.product.category;
  const categoryParts: string[] = [];
  if (category) {
    if (category.parents?.length) {
      category.parents.forEach(p => {
        const s = getLocalizedValue(p, 'slug', backendLocale);
        if (s) {
          categoryParts.push(s);
        }
      });
    }
    const catSlug = getLocalizedValue(category, 'slug', backendLocale);
    if (catSlug) {
      categoryParts.push(catSlug);
    }
  }
  const detailHref = buildRoute('product', ...categoryParts, productSlug);
  return <>
      <div className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
        {/* Top row: image + info always side by side */}
        <div className="flex items-start gap-4">
          {/* Product image */}
          <Link href={detailHref} className="relative block aspect-4/3 w-20 shrink-0 overflow-hidden rounded">
            {item.product.mainPicture ? <LazyImage src={getResizedPictureUrl(item.product.mainPicture.path, 200, 150)} alt={productTitle} fill sizes="80px" className="object-cover" /> : <div className="bg-muted flex h-full w-full items-center justify-center">
                <NoImagePlaceholder iconSize={5} />
              </div>}
          </Link>

          {/* Product info */}
          <div className="flex flex-1 flex-col gap-1">
            <Link href={detailHref} className="font-semibold text-gray-900 hover:underline">
              {productTitle}
            </Link>
            <p className="text-muted-foreground text-xs">{item.product.code}</p>
            {price && <PriceDisplay gross={price.gross} originalGross={price.originalGross} discountRate={price.discountRate} currencyCode={price.currencyCode} locale={backendLocale} size="sm" className="items-start" />}
          </div>

          {/* Desktop: quantity + remove inline */}
          <div className="hidden shrink-0 items-center gap-2 sm:flex">
            <NumberInput value={qty} onValueChange={value => setQty(value ?? 1)} min={1} max={item.product.stock} stepper={1} className="w-28!" />
            <Button size="icon" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive shrink-0" disabled={removing} onClick={() => setDeleteOpen(true)}>
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mobile: quantity + remove below */}
        <div className="flex items-center justify-end gap-2 sm:hidden">
          <div className="w-28 shrink-0">
            <NumberInput value={qty} onValueChange={value => setQty(value ?? 1)} min={1} max={item.product.stock} stepper={1} />
          </div>
          <Button size="icon" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive shrink-0" disabled={removing} onClick={() => setDeleteOpen(true)}>
            <Trash2Icon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Remove confirm dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('remove')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)]">
            <div className="px-5 pr-3 pb-4">
              <p className="text-muted-foreground text-sm">
                [{item.product.code}] {productTitle} ×{item.quantity}
              </p>
            </div>
          </ScrollArea>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={removing}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleRemove} disabled={removing}>
              {t('remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
};
export default CartItemCard;